/**
 * Lazy loading of every non-English catalog. Each `<locale>/<namespace>.json` is its own chunk,
 * fetched only when that locale is shown, and cached by the browser like any hashed asset.
 *
 * A missing or broken file never blocks the game (NFR): its namespace is reported as failed and
 * every key in it falls back to English.
 */
import { I18N_NAMESPACES, type I18nNamespace, type LocaleCode } from "@shared/i18n/localeConfig";
import type { Catalog } from "./englishCatalog";

type CatalogModule = { default: Record<string, unknown> };

// English is bundled eagerly (englishCatalog.ts), so it is left out of the lazy chunks.
const LAZY_CATALOGS = import.meta.glob<CatalogModule>([
  "../../../shared/i18n/locales/*/*.json",
  "!../../../shared/i18n/locales/en/*.json",
]);

function moduleKey(locale: string, namespace: string): string {
  return `../../../shared/i18n/locales/${locale}/${namespace}.json`;
}

export interface LoadedCatalog {
  readonly catalog: Catalog;
  readonly failedNamespaces: readonly I18nNamespace[];
}

/** Only string values are messages; anything else in a file is ignored (and flagged by validation). */
function stringsOnly(raw: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(Object.entries(raw).filter((e): e is [string, string] => typeof e[1] === "string"));
}

export async function loadLocaleCatalog(
  locale: LocaleCode,
  loaders: Readonly<Record<string, () => Promise<CatalogModule>>> = LAZY_CATALOGS,
): Promise<LoadedCatalog> {
  const failed: I18nNamespace[] = [];
  const parts = await Promise.all(
    I18N_NAMESPACES.map(async (ns) => {
      const load = loaders[moduleKey(locale, ns)];
      if (!load) {
        failed.push(ns);
        return {};
      }
      try {
        const mod = await load();
        return mod.default && typeof mod.default === "object" ? stringsOnly(mod.default) : {};
      } catch {
        failed.push(ns);
        return {};
      }
    }),
  );
  return { catalog: Object.freeze(Object.assign({}, ...parts)), failedNamespaces: failed };
}
